import axios from 'axios';

// Get the base URL from environment variables, with a fallback
// Normalize so that when VITE_API_BASE_URL is '/api', we keep axios baseURL empty ('')
// and continue to call endpoints as '/api/...'. For full hosts, keep them as-is.
const RAW_BASE = import.meta.env.VITE_API_BASE_URL;
let resolvedBase = RAW_BASE === '/api' ? '' : (RAW_BASE || 'https://api.suuq-s.com');

// If running on the admin site host and env points to '/api' (same-origin),
// force using the dedicated API host to avoid reverse-proxy auth issues.
try {
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const isAdminSite = /^admin\.suuq-s\.com$/i.test(host);
    if (isAdminSite && (!resolvedBase || resolvedBase === '')) {
      resolvedBase = 'https://api.suuq-s.com';
    }
  }
} catch {
  // Silently continue if window is not available.
}

// Note: we do not force same-origin on the production admin host.
// This allows the admin app to call the API host directly if the site proxy drops Authorization.

export const API_BASE_URL = resolvedBase;

const api = axios.create({
  baseURL: API_BASE_URL,
});

function getJwtExpMs(token) {
  try {
    const p = token.split('.')[1];
    const json = JSON.parse(atob(p));
    if (!json || typeof json.exp !== 'number') return null;
    return json.exp * 1000;
  } catch {
    return null;
  }
}

function isTokenExpired(token, skewMs = 15000) {
  const expMs = getJwtExpMs(token);
  if (!expMs) return false;
  return Date.now() + skewMs >= expMs;
}

// This interceptor will run before every request is sent.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    // Avoid sending Authorization on auth endpoints (login/verify) to reduce preflight friction
    const urlPath = (() => {
      try {
        // Support absolute or relative URLs
        const u = new URL(config.url || '', API_BASE_URL);
        return u.pathname;
      } catch {
        return config?.url || '';
      }
    })();

    // Only skip Authorization for the login endpoint; allow token on verify and all other API calls.
    const isLoginEndpoint = /\/api\/auth\/login\b/.test(urlPath);
    const isApiCall = /^(?:\/)?api\//.test(urlPath);
    if (token && !isLoginEndpoint && isApiCall) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Note: avoid setting client-side cache-control request headers to prevent CORS preflight rejections
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Centralized 401 handling.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Auto-logout only if the stored token is actually expired.
      try {
        const token = localStorage.getItem('accessToken');
        if (token && isTokenExpired(token)) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          if (typeof window !== 'undefined' && window.location.pathname !== '/') {
            window.location.href = '/';
          }
          return Promise.reject(error);
        }
      } catch {
        // Error parsing token, continue.
      }

      // Otherwise only auto-logout for session verification failures.
      try {
        const urlPath = (() => {
          try {
            const u = new URL(error.config?.url || '', API_BASE_URL);
            return u.pathname || '';
          } catch {
            return error.config?.url || '';
          }
        })();
        const isVerify = /\/api\/auth\/verify\b/.test(urlPath);
        if (isVerify) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          if (typeof window !== 'undefined' && window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
      } catch {
        // Error parsing URL, continue.
      }
    }
    return Promise.reject(error);
  }
);

export default api;
